import { useState, useEffect, useRef } from "react";
import {
  FileText,
  Search,
  Filter,
  Download,
  Send,
  Eye,
  X,
  CheckCircle,
  Clock,
  Loader2,
  Mail,
  User,
  CheckCheck,
  Ban,
  Trash2,
  ChevronDown,
  CreditCard,
} from "lucide-react";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import Price from "../../components/Currency/Price";
import LoadingComponent from "../../components/LoadingComponent";
import ConfirmModal from "../../components/Modals/Confirm";
import { Order } from "../../types/Order";
import ContactInfo from "../../components/ContactInfo";
import MyFatoorahPayment from "../MyFatoorahPayment";
// import { refundPayment } from "../../utils/paymentUtils";
import { Payments, Pending, Summarize } from "@mui/icons-material";
import Pagination from "../../components/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

export interface Invoice {
  id: string;
  invoiceNumber: string;
  orderId: string;
  orderNumber: string;
  order: Order;
  customerId: string;
  total: number;
  status: "draft" | "sent" | "paid" | "cancelled";
  dueDate: string;
  issueDate: string;
  paidAt?: string;
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface InvoiceStatsOverview {
  total: number;
  byStatus: {
    draft: number;
    sent: number;
    paid: number;
    cancelled: number;
  };
}

const InvoiceManagementPage = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filtered, setFiltered] = useState<Invoice[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const latestRequestRef = useRef(0);
  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<"invoiceDate" | "amount">("invoiceDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null);
  const [invoiceStatsOverview, setInvoiceStatsOverview] =
    useState<InvoiceStatsOverview | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Bulk send states
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [bulkSendLoading, setBulkSendLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const { i18n, t } = useTranslation();
  const { success, err } = useToast();

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: t("Confirm"),
    cancelText: t("Cancel"),
    onConfirm: () => {},
  });

  const invoiceStatuses = [
    {
      value: "draft",
      label: "Draft",
      color: "bg-yellow-100 text-yellow-800",
      icon: Clock,
    },
    {
      value: "sent",
      label: "Sent",
      color: "bg-blue-100 text-blue-800",
      icon: Send,
    },
    {
      value: "paid",
      label: "Paid",
      color: "bg-green-100 text-green-800",
      icon: CheckCircle,
    },
    {
      value: "cancelled",
      label: "Cancelled",
      color: "bg-gray-100 text-gray-800",
      icon: Ban,
    },
  ];

  useEffect(() => {
    fetchInvoiceStatsOverview();
  }, []);

  useEffect(() => {
    filterInvoices();
  }, [invoices, debouncedSearch, statusFilter]);

  // Manage select all state
  useEffect(() => {
    const selectableCount = filtered.filter(
      (inv) => inv.status !== "cancelled",
    ).length;

    if (selectableCount > 0 && selectedInvoices.length === selectableCount) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedInvoices, filtered]);

  // Clear selection when filter changes
  useEffect(() => {
    setSelectedInvoices([]);
    setSelectAll(false);
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchInvoices();
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    statusFilter,
    sortBy,
    sortOrder,
  ]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const fetchInvoiceStatsOverview = async () => {
    try {
      const res = await BaseApi.get("/invoices/stats/overview");
      if (res?.success && res?.data) {
        setInvoiceStatsOverview({
          total: Number(res.data.total) || 0,
          byStatus: {
            draft: Number(res.data.byStatus?.draft) || 0,
            sent: Number(res.data.byStatus?.sent) || 0,
            paid: Number(res.data.byStatus?.paid) || 0,
            cancelled: Number(res.data.byStatus?.cancelled) || 0,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching invoice stats overview:", error);
    }
  };

  const fetchInvoices = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      const res = await BaseApi.get(`/invoices?${params.toString()}`);
      if (requestId !== latestRequestRef.current) return;
      if (res.success) {
        setInvoices(res.data || []);
        setFiltered(res.data || []);
        setApiTotalItems(res.pagination?.totalItems || res.data.length);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching invoices:", error);
      err(t("Failed to load invoices"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const filterInvoices = () => {
    let result = [...invoices];

    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (invoice) =>
          invoice.invoiceNumber?.toLowerCase().includes(searchLower) ||
          invoice.orderNumber?.toLowerCase().includes(searchLower) ||
          invoice.order?.customer?.firstName
            ?.toLowerCase()
            .includes(searchLower) ||
          invoice.order?.customer?.lastName
            ?.toLowerCase()
            .includes(searchLower) ||
          invoice.order?.customer?.email?.toLowerCase().includes(searchLower),
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((invoice) => invoice.status === statusFilter);
    }

    setFiltered(result);
  };

  // const handleRefund = async (invoice: Invoice) => {
  //   try {
  //     await refundPayment({
  //       orderId: invoice.orderId,
  //       amount: invoice.total,
  //       reason: "Customer requested refund",
  //     });
  //     // Refresh invoice list
  //   } catch (error) {
  //     console.error("Refund Failed ", error);
  //     err("Refund Failed");
  //   }
  // };

  const handleDownloadInvoice = async (invoiceId: string) => {
    window.open(
      `${process.env.REACT_APP_API_BASE_URL}/invoices/${invoiceId}/download`,
      "_blank",
    );
    // try {
    //   setActionLoading(invoiceId);
    //   const res = await BaseApi.get(`/invoices/${invoiceId}/download`, {
    //     responseType: "blob",
    //   });

    //   const url = window.URL.createObjectURL(new Blob([res]));
    //   const link = document.createElement("a");
    //   link.href = url;
    //   link.setAttribute("download", `invoice-${invoiceId}.pdf`);
    //   document.body.appendChild(link);
    //   link.click();
    //   link.remove();

    //   success(t("Invoice downloaded successfully"));
    // } catch (error) {
    //   console.error("Error downloading invoice:", error);
    //   err(t("Failed to download invoice"));
    // } finally {
    //   setActionLoading(null);
    // }
  };

  const handleSendInvoice = async (invoiceId: string) => {
    try {
      setActionLoading(invoiceId);
      const res = await BaseApi.post(`/invoices/${invoiceId}/send`, {
        method: "both",
        language: i18n.language,
      });

      if (res.success) {
        setInvoices(
          invoices.map((inv) =>
            inv.id === invoiceId
              ? { ...inv, sentAt: new Date().toISOString() }
              : inv,
          ),
        );
        success(t("Invoice sent successfully"));
      }
    } catch (error) {
      console.error("Error sending invoice:", error);
      err(t("Failed to send invoice"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkPaid = async (invoiceId: string) => {
    try {
      setActionLoading(invoiceId);
      const res = await BaseApi.patch(`/invoices/${invoiceId}/mark-paid`);

      if (res.success) {
        setInvoices(
          invoices.map((inv) =>
            inv.id === invoiceId
              ? {
                  ...inv,
                  status: "paid" as const,
                  paidAt: new Date().toISOString(),
                }
              : inv,
          ),
        );
        if (selectedInvoice?.id === invoiceId) {
          setSelectedInvoice({
            ...selectedInvoice,
            status: "paid" as const,
            paidAt: new Date().toISOString(),
          });
        }
        success(t("Invoice marked as paid"));
      }
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      err(t("Failed to mark invoice as paid"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInvoice = (invoiceId: string) => {
    openConfirmModal({
      title: t("Cancel Invoice"),
      message: t("Are you sure you want to cancel this invoice?"),
      confirmText: t("Cancel"),
      onConfirm: async () => {
        try {
          setActionLoading(invoiceId);
          const res = await BaseApi.patch(`/invoices/${invoiceId}/cancel`);

          if (res.success) {
            setInvoices(
              invoices.map((inv) =>
                inv.id === invoiceId
                  ? { ...inv, status: "cancelled" as const }
                  : inv,
              ),
            );
            if (selectedInvoice?.id === invoiceId) {
              setSelectedInvoice({
                ...selectedInvoice,
                status: "cancelled" as const,
              });
            }
            success(t("Invoice cancelled successfully"));
          }
        } catch (error) {
          console.error("Error cancelling invoice:", error);
          err(t("Failed to cancel invoice"));
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  // MyFatoorah Payment Integration
  const handleProceedToPayment = async (invoice: Invoice) => {
    setPaymentInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setPaymentInvoice(null);
    success(t("Payment completed successfully!"));

    // Refresh invoices to get updated status
    await fetchInvoices();

    // If the details modal is open, close it
    if (detailsModal) {
      setDetailsModal(false);
      setSelectedInvoice(null);
    }
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setPaymentInvoice(null);
  };

  const handlePaymentError = (error: string) => {
    console.error("Payment Error: ", error);
    // err(error);
  };

  const needsPayment = (invoice: Invoice) => {
    // console.log("needsPayment - ", invoice);
    return (
      invoice.status === "sent" &&
      invoice.order?.paymentStatus === "pending" &&
      invoice.order?.status !== "cancelled"
    );
  };

  // Bulk send functionality
  const handleBulkSendInvoices = async () => {
    if (selectedInvoices.length === 0) {
      err(t("Please select at least one invoice to send"));
      return;
    }

    openConfirmModal({
      title: t("Send Bulk Invoice"),
      message: t(
        `Are you sure you want to send ${selectedInvoices.length} invoice(s) to customers?`,
      ),
      confirmText: t("Cancel"),
      onConfirm: async () => {
        try {
          setBulkSendLoading(true);

          // Call bulk send API
          const res = await BaseApi.post("/invoices/bulk-send", {
            invoiceIds: selectedInvoices,
          });

          if (res.success) {
            // Update sent status for all selected invoices
            const now = new Date().toISOString();
            setInvoices(
              invoices.map((inv) =>
                selectedInvoices.includes(inv.id)
                  ? { ...inv, sentAt: now }
                  : inv,
              ),
            );

            success(
              t(
                `Successfully sent ${selectedInvoices.length} invoice(s) to customers`,
              ),
            );

            // Clear selection
            setSelectedInvoices([]);
            setSelectAll(false);
          }
        } catch (error) {
          console.error("Error sending bulk invoices:", error);
          err(
            t("Failed to send invoices. Some may have been sent successfully."),
          );
        } finally {
          setBulkSendLoading(false);
        }
      },
    });
  };

  const handleSelectInvoice = (invoiceId: string) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId],
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedInvoices([]);
    } else {
      // Only select invoices that can be sent (not cancelled)
      const selectableIds = filtered
        .filter((inv) => inv.status !== "cancelled")
        .map((inv) => inv.id);
      setSelectedInvoices(selectableIds);
    }
    setSelectAll(!selectAll);
  };

  const openConfirmModal = (config: {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
  }) => {
    setConfirmConfig({
      isOpen: true,
      title: config.title ?? t("Confirm Action"),
      message: config.message ?? t("Are you sure?"),
      confirmText: config.confirmText ?? "Confirm",
      cancelText: config.cancelText ?? "Cancel",
      onConfirm: () => {
        config.onConfirm();
        closeConfirmModal();
      },
    });
  };

  const closeConfirmModal = () => {
    setConfirmConfig((c) => ({ ...c, isOpen: false }));
  };

  // const handleCancel = async (invoiceId: string) => {
  //   try {
  //     setActionLoading(invoiceId);
  //     const res = await BaseApi.patch(`/invoices/${invoiceId}/cancel`);

  //     if (res.success) {
  //       setInvoices(
  //         invoices.map((inv) =>
  //           inv.id === invoiceId
  //             ? { ...inv, status: "cancelled" as const }
  //             : inv,
  //         ),
  //       );
  //       if (selectedInvoice?.id === invoiceId) {
  //         setSelectedInvoice({
  //           ...selectedInvoice,
  //           status: "cancelled" as const,
  //         });
  //       }
  //       success(t("Invoice cancelled successfully"));
  //     }
  //   } catch (error) {
  //     console.error("Error cancelling invoice:", error);
  //     err(t("Failed to cancel invoice"));
  //   } finally {
  //     setActionLoading(null);
  //   }
  // };

  const handleViewDetails = async (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDetailsModal(true);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";

    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    return (
      invoiceStatuses.find((s) => s.value === status)?.color ||
      "bg-gray-100 text-gray-800"
    );
  };

  // Pagination calculations
  const finalTotalItems = apiTotalItems;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);
  const currentPageData = invoices;

  const invoiceStats = {
    total: invoiceStatsOverview?.total ?? invoices.length,
    draft:
      invoiceStatsOverview?.byStatus.draft ??
      invoices.filter((i) => i.status === "draft").length,
    paid:
      invoiceStatsOverview?.byStatus.paid ??
      invoices.filter((i) => i.status === "paid").length,
    sent:
      invoiceStatsOverview?.byStatus.sent ??
      invoices.filter((i) => i.status === "sent").length,
    cancelled:
      invoiceStatsOverview?.byStatus.cancelled ??
      invoices.filter((i) => i.status === "cancelled").length,
  };

  if (initialLoading) {
    return <LoadingComponent title={t("Invoices")} />;
  }

  const showStats = (
    Icon: any,
    title: string,
    value = 0,
    color = "black",
    isMoney = false,
    iconClassName = "h-8 w-8",
  ) => {
    return (
      <div
        className={`bg-${color}-50 rounded-xl shadow-md p-2 sm:p-3 cursor-pointer hover:shadow-lg transition-shadow`}
      >
        <div className={`flex flex-row justify-between rounded-lg sm:p-4`}>
          <div
            className={`flex items-center gap-2 whitespace-nowrap text-lg text-${color}-600`}
          >
            <Icon className={`${iconClassName} shrink-0 text-${color}-600`} />
            <span>{title}</span>
          </div>
          <div className={`text-xl sm:text-2xl font-bold text-${color}-900`}>
            {isMoney ? <Price amount={value} /> : value}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f2e6db] py-4 sm:py-8 px-3 sm:px-4 lg:px-6">
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div>
          <div className="g:flex-row items-start lg:items-center justify-between gap-6 py-4">
            <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border border-gray-100">
              <div className="flex items-center gap-5">
                <div className="relative">
                  <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                    <FileText className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-4xl font-bold gradient-text mb-1">
                    {t("Invoices")}
                  </h1>
                  <p className="text-gray-600 text-sm">{t("invoice-text")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 my-2 sm:my-3">
            {showStats(Summarize, t("Total"), invoiceStats.total, "gray")}
            {showStats(Pending, t("Draft"), invoiceStats.draft, "yellow")}
            {showStats(Payments, t("Paid"), invoiceStats.paid, "green")}
            {showStats(
              Send,
              t("Sent"),
              invoiceStats.sent,
              "blue",
              false,
              "h-5 w-5",
            )}
            {showStats(
              Ban,
              t("Cancelled"),
              invoiceStats.cancelled,
              "red",
              false,
              "h-5 w-5",
            )}
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedInvoices.length > 0 && (
          <div className="bg-[#0d223f] text-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 my-2 sm:my-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <span className="font-semibold text-sm sm:text-base">
                  {selectedInvoices.length} {t("invoice(s) selected")}
                </span>
                <button
                  onClick={() => {
                    setSelectedInvoices([]);
                    setSelectAll(false);
                  }}
                  className="text-xs sm:text-sm underline hover:no-underline"
                >
                  {t("Clear selection")}
                </button>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleBulkSendInvoices}
                  disabled={bulkSendLoading}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium disabled:opacity-50 text-sm"
                >
                  {bulkSendLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="hidden sm:inline">
                        {t("Sending...")}
                      </span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span className="hidden sm:inline">
                        {t("Send Selected Invoices")}
                      </span>
                      <span className="sm:hidden">{t("Send")}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 my-2 sm:mb-4">
          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4"
          >
            <span className="flex items-center gap-2 font-medium text-sm sm:text-base">
              <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
              {t("Filters")}
            </span>
            <ChevronDown
              className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform ${showFilters ? "rotate-180" : ""}`}
            />
          </button>

          {/* Filter Inputs */}
          <div
            className={`grid grid-cols-1 gap-3 sm:gap-4 ${showFilters ? "block" : "hidden"} lg:grid lg:grid-cols-2`}
          >
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder={t("Search invoices...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent appearance-none bg-white"
              >
                <option value="all">{t("All Status")}</option>
                {invoiceStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Invoices - Desktop Table & Mobile Cards */}
        <div
          className={
            "bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden" +
            (loading ? " opacity-50 pointer-events-none transition-opacity" : "")
          }
        >
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Invoice")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Order")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Customer")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Amount")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Issue Date")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Updated Date")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Status")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentPageData.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedInvoices.includes(invoice.id)
                        ? "bg-[#0d223f]"
                        : ""
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => handleSelectInvoice(invoice.id)}
                        disabled={invoice.status === "cancelled"}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[120px] ">
                        {invoice.invoiceNumber}
                      </div>
                      {invoice.sentAt && (
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Mail className="w-3 h-3" />
                          {t("Sent")}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 truncate max-w-[180px] gap-1">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                          {invoice.orderNumber || invoice.order.orderNumber}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[180px]">
                          Payment Status:{" "}
                          <span
                            className={`px-2 py-2 capitalize rounded-full text-xs font-medium ${getStatusColor(
                              invoice.order.paymentStatus,
                            )}`}
                          >
                            {invoice.order.paymentStatus}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[180px]">
                          Order Status:{" "}
                          <span
                            className={`px-2 py-2 capitalize rounded-full text-xs font-medium ${getStatusColor(
                              invoice.order.status,
                            )}`}
                          >
                            {invoice.order.status}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                        {invoice?.order?.customer?.firstName ||
                        invoice?.order?.customer?.lastName
                          ? `${invoice.order.customer.firstName || ""} ${invoice.order.customer.lastName || ""}`.trim()
                          : invoice?.customerId}
                      </div>
                      {invoice?.order?.customer?.phone && (
                        <div className="text-xs text-gray-500">
                          <ContactInfo
                            type="phone"
                            value={invoice.order.customer.phone}
                            label={t("Phone")}
                          />
                        </div>
                      )}
                      {invoice?.order?.customer?.email && (
                        <div className="text-xs text-gray-500">
                          <ContactInfo
                            type="email"
                            value={invoice.order.customer.email}
                            label={t("Email")}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        <Price amount={Number(invoice.total)} />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(invoice.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(invoice.updatedAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 capitalize rounded-full text-xs font-medium ${getStatusColor(
                          invoice.status,
                        )}`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetails(invoice)}
                          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                          title={t("View Details")}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(invoice.id)}
                          disabled={actionLoading === invoice.id}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                          title={t("Download")}
                        >
                          {actionLoading === invoice.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleSendInvoice(invoice.id)}
                          disabled={actionLoading === invoice.id}
                          className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                          title={t("Send Email")}
                        >
                          {actionLoading === invoice.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </button>

                        {needsPayment(invoice) && (
                          <button
                            onClick={() => handleProceedToPayment(invoice)}
                            disabled={paymentLoading === invoice.id}
                            className="p-2 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                            title={t("Proceed to Pay")}
                          >
                            {paymentLoading === invoice.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CreditCard className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        {invoice.status === "sent" && (
                          <button
                            onClick={() => handleMarkPaid(invoice.id)}
                            disabled={actionLoading === invoice.id}
                            className="p-2 text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                            title={t("Mark as Paid")}
                          >
                            {actionLoading === invoice.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCheck className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        {invoice.status !== "cancelled" &&
                          invoice.status !== "paid" && (
                            <button
                              onClick={() => handleCancelInvoice(invoice.id)}
                              disabled={actionLoading === invoice.id}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title={t("Cancel Invoice")}
                            >
                              {actionLoading === invoice.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Ban className="w-4 h-4" />
                              )}
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden divide-y divide-gray-200">
            {currentPageData.map((invoice) => (
              <div
                key={invoice.id}
                className={`p-4 hover:bg-gray-50 transition-colors ${
                  selectedInvoices.includes(invoice.id) ? "bg-[#0d223f]" : ""
                }`}
              >
                {/* Card Header with Checkbox */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.includes(invoice.id)}
                      onChange={() => handleSelectInvoice(invoice.id)}
                      disabled={invoice.status === "cancelled"}
                      className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer disabled:opacity-30"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-bold text-gray-900 mb-1">
                        {invoice.invoiceNumber}
                      </div>
                      <div className="text-xs text-gray-600">
                        {invoice?.order?.customer?.firstName ||
                        invoice?.order?.customer?.lastName
                          ? `${invoice.order.customer.firstName || ""} ${invoice.order.customer.lastName || ""}`.trim()
                          : invoice?.customerId}
                      </div>
                      {invoice.sentAt && (
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Mail className="w-3 h-3" />
                          {t("Sent")}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewDetails(invoice)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                </div>

                {/* Card Details Grid */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">
                      {t("Amount")}
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      <Price amount={Number(invoice.total)} />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">
                      {t("Order")}
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {invoice.order.orderNumber || invoice.orderId}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">
                      {t("Issue Date")}
                    </div>
                    <div className="text-sm text-gray-900">
                      {formatDate(invoice.createdAt)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">
                      {t("Due Date")}
                    </div>
                    <div className="text-sm text-gray-900">
                      {formatDate(invoice.dueDate)}
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mb-3">
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      invoice.status,
                    )}`}
                  >
                    {invoice.status}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleDownloadInvoice(invoice.id)}
                    disabled={actionLoading === invoice.id}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 text-xs font-medium"
                  >
                    {actionLoading === invoice.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {t("Download")}
                  </button>
                  <button
                    onClick={() => handleSendInvoice(invoice.id)}
                    disabled={actionLoading === invoice.id}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 text-xs font-medium"
                  >
                    {actionLoading === invoice.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {t("Send")}
                  </button>
                  {needsPayment(invoice) && (
                    <button
                      onClick={() => handleProceedToPayment(invoice)}
                      disabled={paymentLoading === invoice.id}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50 text-xs font-medium"
                    >
                      {paymentLoading === invoice.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4" />
                      )}
                      {t("Pay Now")}
                    </button>
                  )}
                  {invoice.status === "sent" && (
                    <button
                      onClick={() => handleMarkPaid(invoice.id)}
                      disabled={actionLoading === invoice.id}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50 text-xs font-medium"
                    >
                      {actionLoading === invoice.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCheck className="w-4 h-4" />
                      )}
                      {t("Paid")}
                    </button>
                  )}
                  {invoice.status !== "cancelled" &&
                    invoice.status !== "paid" && (
                      <button
                        onClick={() => handleCancelInvoice(invoice.id)}
                        disabled={actionLoading === invoice.id}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 text-xs font-medium"
                      >
                        {actionLoading === invoice.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Ban className="w-4 h-4" />
                        )}
                        {t("Cancel")}
                      </button>
                    )}
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 px-4">
              <FileText className="mx-auto w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
              <p className="mt-4 text-sm sm:text-base text-gray-600">
                {t("No invoices found")}
              </p>
            </div>
          )}

          {/* Pagination Controls */}
          {finalTotalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={finalTotalPages}
              totalItems={finalTotalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              showPageSizeSelector={true}
              isLoading={loading}
              compact={true}
            />
          )}
        </div>
      </div>

      {/* Invoice Details Modal */}
      {detailsModal && selectedInvoice && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50 overflow-y-auto"
          onClick={() => {
            setDetailsModal(false);
            setSelectedInvoice(null);
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-2xl my-4 max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <div>
                <h3 className="text-base sm:text-xl font-bold text-white">
                  {t("Invoice Details")}
                </h3>
                <p className="text-xs sm:text-sm text-gray-300">
                  {selectedInvoice.invoiceNumber}
                </p>
              </div>
              <button
                onClick={() => {
                  setDetailsModal(false);
                  setSelectedInvoice(null);
                }}
                className="text-white hover:text-gray-300 p-1"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Status & Actions */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div>
                  <span
                    className={`inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(
                      selectedInvoice.status,
                    )}`}
                  >
                    {selectedInvoice.status.toUpperCase()}
                  </span>
                  {selectedInvoice.paidAt && (
                    <p className="text-xs sm:text-sm text-gray-600 mt-2">
                      {t("Paid on")} {formatDate(selectedInvoice.paidAt)}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => handleDownloadInvoice(selectedInvoice.id)}
                    disabled={actionLoading === selectedInvoice.id}
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-[#0d223f] text-white rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 text-sm"
                  >
                    {actionLoading === selectedInvoice.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {t("Download")}
                  </button>
                  {needsPayment(selectedInvoice) && (
                    <button
                      onClick={() => handleProceedToPayment(selectedInvoice)}
                      disabled={paymentLoading === selectedInvoice.id}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm"
                    >
                      {paymentLoading === selectedInvoice.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4" />
                      )}
                      {t("Proceed to Pay")}
                    </button>
                  )}
                  {selectedInvoice.status === "sent" && (
                    <button
                      onClick={() => handleMarkPaid(selectedInvoice.id)}
                      disabled={actionLoading === selectedInvoice.id}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
                    >
                      {actionLoading === selectedInvoice.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCheck className="w-4 h-4" />
                      )}
                      {t("Mark Paid")}
                    </button>
                  )}
                </div>
              </div>

              {/* Invoice Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {t("Invoice Number")}
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {selectedInvoice.invoiceNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {t("Order Number")}
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {selectedInvoice.orderNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {t("Issue Date")}
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {formatDate(selectedInvoice.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {t("Due Date")}
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {formatDate(selectedInvoice.dueDate)}
                  </p>
                </div>
              </div>

              {/* Customer Information */}
              <div className="border rounded-lg p-3 sm:p-4 bg-gray-50">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <User size={16} />
                  {t("Customer Information")}
                </h4>
                <div className="space-y-2 text-xs sm:text-sm">
                  <p>
                    <span className="text-gray-600">{t("Name")}:</span>
                    <span className="ml-2 font-medium">
                      {selectedInvoice?.order?.customer?.firstName ||
                      selectedInvoice?.order?.customer?.lastName
                        ? `${selectedInvoice.order.customer.firstName || ""} ${selectedInvoice.order.customer.lastName || ""}`.trim()
                        : selectedInvoice?.customerId}
                    </span>
                  </p>
                  <p className="break-all">
                    <span className="ml-2 font-medium">
                      <ContactInfo
                        type="email"
                        value={selectedInvoice?.order?.customer?.email}
                      />
                    </span>
                  </p>
                  <p className="break-all">
                    <span className="ml-2 font-medium">
                      <ContactInfo
                        type="phone"
                        value={selectedInvoice?.order?.customer?.phone}
                      />
                    </span>
                  </p>
                </div>
              </div>

              {/* Payment Status Info */}
              {selectedInvoice.order && (
                <div className="border rounded-lg p-3 sm:p-4 bg-gray-50">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <CreditCard size={16} />
                    {t("Payment Information")}
                  </h4>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <p>
                      <span className="text-gray-600">
                        {t("Payment Status")}:
                      </span>
                      <span
                        className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedInvoice.order.paymentStatus)}`}
                      >
                        {selectedInvoice.order.paymentStatus}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-600">
                        {t("Order Status")}:
                      </span>
                      <span
                        className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedInvoice.order.status)}`}
                      >
                        {selectedInvoice.order.status}
                      </span>
                    </p>
                  </div>
                </div>
              )}
              {showPaymentModal && paymentInvoice && (
                <MyFatoorahPayment
                  orderId={paymentInvoice.orderId}
                  amount={paymentInvoice.total}
                  paymentMethod={paymentInvoice.order?.paymentMethod || "card"}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                  onError={handlePaymentError}
                />
              )}

              {/* Amount */}
              <div className="border rounded-lg p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex justify-between items-center">
                  <span className="text-base sm:text-lg font-semibold text-gray-700">
                    {t("Invoice Amount")}:
                  </span>
                  <span className="text-xl sm:text-2xl font-bold text-gray-900">
                    <Price amount={Number(selectedInvoice.total)} />
                  </span>
                </div>
              </div>

              {/* Additional Info */}
              {selectedInvoice.sentAt && (
                <div className="text-xs sm:text-sm text-gray-600 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {t("Sent to customer on")}{" "}
                  {formatDate(selectedInvoice.sentAt)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        icon={Trash2}
        iconColor="text-red-600"
        iconBg="bg-red-100"
        onConfirm={confirmConfig.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
};

export default InvoiceManagementPage;
